import React, { useContext, useMemo } from 'react';
import { useHistory, Link, useLocation } from 'react-router-dom';
import {
  PageContainer,
  PageHeader,
  ContentCard,
  EmptyState,
} from '../../../../components/layout';
import { Table, Pagination } from '../../../../components/data';
import Button from '../../../../components/common/Button';
import Badge from '../../../../components/common/Badge';
import AvatarName from '../../../../components/ui/AvatarName';
import FilterIncident from './FilterIncident';
import SelectedFilters from './SelectedFilters';
import useIncidentsList from '../useIncidentsList';
import {
  translate,
  getNameIncidentsStatus,
  getIconStatusIncident,
  getColorStatusIncident,
  getColorPriorityIncident,
  getFlagPriorityIncident,
  getNameIncidentsPriority,
  getNameIncidentsTypes,
  getFormatDate,
} from '../../../../utils/Common';
import AuthContext from '../../../../contexts/auth';
import { LuAlertCircle } from 'react-icons/lu';
import { TfiExport } from 'react-icons/tfi';
import { LuFileBarChart } from 'react-icons/lu';
import { FaEye } from 'react-icons/fa';
import { IoFilterSharp, IoRefreshOutline } from 'react-icons/io5';
import { PiLockersFill } from 'react-icons/pi';
import Spinner from '../../../../components/Spinner';
import styles from './IncidentsList.module.css';
import { verifyAccessViewPermissions } from '../../../../config/permissions';

const statusColorToBadgeVariant = {
  'bg-blue-500': 'info',
  'bg-yellow-500': 'warning',
  'bg-orange-500': 'warning',
  'bg-green-500': 'success',
  'bg-gray-500': 'neutral',
  'bg-red-500': 'error',
  'bg-purple-500': 'archived',
};

const priorityToBadgeVariant = {
  danger: 'error',
  warning: 'warning',
  primary: 'info',
};

const ListIncidents = () => {
  const history = useHistory();
  const location = useLocation();
  const userContext = useContext(AuthContext);
  const isAdmin = userContext.user?.IsAdmin;
  const permissionsPath = "/dashboard/incidents";
  const canSearch = verifyAccessViewPermissions(["search"], {
    path: permissionsPath || location.pathname,
  });
  const canExportAction = verifyAccessViewPermissions(["export"], {
    path: permissionsPath || location.pathname,
  });

  const {
    incidents,
    loading,
    error,
    msgError,
    statusIncidents,
    filters,
    handleFilter,
    handleResetFilters,
    handleStatusClick,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleExportIncidents,
    exporting,
    pagination,
  } = useIncidentsList();

  const columns = useMemo(() => {
    const cols = [
      {
        key: 'IdIncidents',
        label: 'ID',
        sortable: true,
        width: '80px',
        render: (row) => `# ${row?.IdIncidents ?? '-'}`,
      },
    ];

    if (isAdmin) {
      cols.push({
        key: 'NameClient',
        label: translate('dashboard.userprofile.business.user'),
        sortable: true,
        width: '150px',
        render: (row) => row?.NameClient ?? '-',
      });
    }

    cols.push(
      {
        key: 'IdType',
        label: translate('global.type'),
        sortable: true,
        render: (row) => (
          <div className={styles.typeCell}>
            <span className={styles.typeLabel}>{getNameIncidentsTypes(row?.IdType) ?? '-'}</span>
            {row?.LockerName && (
              <div className={styles.lockerWrapper} title={row.LockerName}>
                <PiLockersFill size={10} className={styles.lockerIcon} />
                <span className={styles.lockerValue}>{row.LockerName}</span>
              </div>
            )}
          </div>
        ),
      },
      {
        key: 'IdPriority',
        label: translate('global.priority'),
        sortable: true,
        width: '120px',
        render: (row) => {
          const variant = priorityToBadgeVariant[getColorPriorityIncident(row?.IdPriority)] ?? 'neutral';
          return (
            <Badge variant={variant} size="sm" icon={getFlagPriorityIncident(row?.IdPriority)}>
              {getNameIncidentsPriority(row?.IdPriority) ?? '-'}
            </Badge>
          );
        },
      },
      {
        key: 'IdStatus',
        label: translate('global.status'),
        sortable: true,
        width: '140px',
        render: (row) => {
          const colorClass = getColorStatusIncident(row?.IdStatus);
          const variant = statusColorToBadgeVariant[colorClass] ?? 'neutral';
          return (
            <Badge variant={variant} size="sm" icon={getIconStatusIncident(row?.IdStatus)}>
              {getNameIncidentsStatus(row?.IdStatus) ?? '-'}
            </Badge>
          );
        },
      },
      {
        key: 'NameClientAssigned',
        label: <span className={styles.clientColumnLabel}>{translate('global.client')}</span>,
        width: '120px',
        render: (row) =>
          row?.NameClientAssigned ? (
            <AvatarName name={row.NameClientAssigned} />
          ) : (
            '-'
          ),
      },
      {
        key: 'DateIncident',
        label: translate('global.dateCreated'),
        sortable: true,
        width: '120px',
        render: (row) =>
          row?.DateIncident ? getFormatDate(row.DateIncident, true) : '-',
      },
      {
        key: 'actions',
        label: translate('global.action'),
        width: '100px',
        render: (row) => (
          <Link
            to={`/dashboard/incidents/${row?.IdIncidents}`}
            onClick={(e) => e.stopPropagation()}
          >
            <Button variant="ghost" size="sm" leftIcon={<FaEye size={14} />}>
              {translate('global.seeMore')}
            </Button>
          </Link>
        ),
      }
    );

    return cols;
  }, [isAdmin]);

  const handleRowClick = (row) => {
    if (row?.IdIncidents) {
      history.push(`/dashboard/incidents/${row.IdIncidents}`);
    }
  };

  const handleExport = async () => {
    if (!canExportAction) return;
    await handleExportIncidents();
  };

  const isEmpty = !loading && (!incidents || incidents.length === 0);
  const emptyMessage = msgError ? translate(msgError) : translate('dashboard.accounting.message.NO_INCIDENTS_FILTERS');
  const canExport = canExportAction && !loading && pagination.totalItems > 0;

  return (
    <PageContainer>
      <PageHeader
        title={translate('global.listIncident')}
        subtitle={
          loading ? (
            <span className={styles.loadingDot}>{translate('global.loading')}</span>
          ) : statusIncidents.length > 0 ? (
            <div className={styles.statsBarWrapper}>
            <div className={styles.statsBar}>
              <span className={styles.statsTotal}>
                {statusIncidents.reduce((sum, i) => sum + (i?.TotalIncidents || 0), 0).toLocaleString('en-US')}{' '}
                {translate('dashboard.clients.stats.total')}
              </span>
              {statusIncidents.map((item) => {
                const isSelected = filters?.statusIncident?.includes(item?.IdStatus);
                const colorClass = getColorStatusIncident(item?.IdStatus);
                const variant = statusColorToBadgeVariant[colorClass] ?? 'neutral';
                return (
                  <button
                    type="button"
                    key={item?.IdStatus}
                    className={`${styles.statBadge} ${isSelected ? styles.statBadgeSelected : ''}`}
                    onClick={() => handleStatusClick(item?.IdStatus)}
                  >
                    <Badge
                      variant={variant}
                      size="sm"
                      icon={getIconStatusIncident(item?.IdStatus)}
                    >
                      {item?.TotalIncidents} {getNameIncidentsStatus(item?.IdStatus)}
                    </Badge>
                  </button>
                );
              })}
            </div>
            </div>
          ) : (
            `${pagination.totalItems ?? 0} ${translate('global.results')}`
          )
        }
        icon={<LuAlertCircle size={28} />}
        actions={
          <div className={styles.headerActions}>
            {canSearch && (
              <Button
                variant="outline"
                size="md"
                leftIcon={<LuFileBarChart size={18} />}
                onClick={() => history.push('/dashboard/incidents/report')}
              >
                {translate('dashboard.incidents.report.listButton')}
              </Button>
            )}
            {canExportAction && (
              <Button
                variant="outline"
                size="md"
                loading={exporting}
                leftIcon={<TfiExport size={18} />}
                onClick={handleExport}
                disabled={!canExport || exporting}
              >
                {translate('global.export')}
              </Button>
            )}
            {canSearch && (
              <FilterIncident
                filterOptions={filters}
                setFilterOptions={handleFilter}
                isLoading={loading}
              />
            )}
          </div>
        }
      />

      {/* Filter chips - full width below divider */}
      {canSearch && !Object.entries(filters).every(([, arr]) => !arr || arr.length === 0) && (
        <div className={styles.filtersChipsSection}>
          <div className={styles.filtersSectionRow}>
            <span className={styles.filtersSectionTitle}>
              <IoFilterSharp size={16} />
              {translate('global.activeFilters')}:
            </span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              leftIcon={<IoRefreshOutline size={16} />}
              onClick={handleResetFilters}
              className={styles.resetFiltersBtn}
            >
              {translate('global.reset')}
            </Button>
          </div>
          <SelectedFilters
            filterOptions={filters}
            setFilterOptions={handleFilter}
          />
        </div>
      )}

      {error && (
        <ContentCard>
          <div className={styles.errorBanner}>
            <strong>{translate('global.error')}:</strong> {error}
          </div>
        </ContentCard>
      )}

      {isEmpty ? (
        <EmptyState
          icon={<LuAlertCircle size={64} />}
          title={translate('global.listIncident')}
          description={emptyMessage}
        />
      ) : (
        <>
          <Table
            columns={columns}
            data={incidents.map((i) => ({ ...i, id: i.IdIncidents }))}
            loading={loading}
            onSort={handleSort}
            onRowClick={handleRowClick}
            emptyMessage={emptyMessage}
            hoverable
          />

          {!loading && incidents.length > 0 && (
            <Pagination
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              totalItems={pagination.totalItems}
              itemsPerPage={pagination.itemsPerPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          )}
        </>
      )}
    </PageContainer>
  );
};

export default ListIncidents;
